import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { accountApi, type AccountState } from '../../api/accountApi';
import { useAuth } from '../../../auth/AuthContext';

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100dvh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(20px, 5vw, 48px)',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    margin: 0,
    padding: '40px 34px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  icon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--red-soft, rgba(248,113,113,0.10))',
    border: '1px solid rgba(248,113,113,0.16)',
    color: 'var(--red, #F87171)',
    marginBottom: 20,
  },
  title: { fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 10, color: 'var(--text, #F3F6FC)' },
  text: { color: 'var(--text-2, #AAB2C5)', marginBottom: 26, lineHeight: 1.55, fontSize: 14.5 },
  actions: { width: '100%', display: 'flex', flexDirection: 'column', gap: 10 },
  button: {
    width: '100%',
    minHeight: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--accent-grad, linear-gradient(135deg, #2E86FF 0%, #45C2FF 100%))',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--r-sm, 14px)',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 16px -2px var(--accent-glow, rgba(46,134,255,0.32))',
  },
  ghost: {
    width: '100%',
    minHeight: 46,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.04)',
    color: 'var(--text-2, #AAB2C5)',
    border: '1px solid var(--border-2, rgba(255,255,255,0.08))',
    borderRadius: 'var(--r-sm, 14px)',
    fontSize: 14.5,
    fontWeight: 600,
    cursor: 'pointer',
  },
  danger: {
    width: '100%',
    minHeight: 46,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--red-soft, rgba(248,113,113,0.10))',
    color: 'var(--red, #F87171)',
    border: '1px solid rgba(248,113,113,0.18)',
    borderRadius: 'var(--r-sm, 14px)',
    fontSize: 14.5,
    fontWeight: 700,
    cursor: 'pointer',
  },
  warn: {
    color: 'var(--red, #F87171)',
    background: 'var(--red-soft, rgba(248,113,113,0.10))',
    border: '1px solid rgba(248,113,113,0.16)',
    borderRadius: 'var(--r-sm, 14px)',
    padding: '12px 14px',
    fontSize: 13,
    lineHeight: 1.5,
    textAlign: 'left',
  },
  logoutRow: { marginTop: 26, paddingTop: 20, width: '100%', borderTop: '1px solid var(--border, rgba(255,255,255,0.07))' },
  logout: {
    background: 'transparent',
    color: 'var(--red, #F87171)',
    border: 'none',
    padding: '8px 0',
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
};

function formatDate(iso: string | null): string {
  if (!iso) return 'ближайшие дни';
  try {
    return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

export function AccountDeletedGate({ state }: { state: AccountState }) {
  const { logout } = useAuth();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries();
  const restore = useMutation({ mutationFn: () => accountApi.restore(), onSuccess: invalidate });
  const reset = useMutation({ mutationFn: () => accountApi.reset(), onSuccess: invalidate });
  const [confirmReset, setConfirmReset] = useState(false);
  const busy = restore.isPending || reset.isPending;

  return (
    <div className="tma2-root" style={styles.root}>
      <div className="sh-card" style={styles.card}>
        <div style={styles.icon}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </div>

        <h1 style={styles.title}>Аккаунт удалён</h1>
        <p style={styles.text}>
          Ваш аккаунт помечен на удаление. Данные ещё хранятся и будут безвозвратно
          стёрты {formatDate(state.purge_at)}.
          <br /><br />
          Вы можете восстановить данные или начать заново.
        </p>

        <div style={styles.actions}>
          <button style={styles.button} disabled={busy} onClick={() => restore.mutate()}>
            {restore.isPending ? 'Восстановление…' : 'Восстановить мои данные'}
          </button>

          {!confirmReset ? (
            <button style={styles.ghost} disabled={busy} onClick={() => setConfirmReset(true)}>
              Начать заново
            </button>
          ) : (
            <>
              <div style={styles.warn}>
                Старые данные будут заархивированы и удалены через {state.retention_days} дн.
                Вы начнёте с чистого аккаунта. Продолжить?
              </div>
              <button style={styles.danger} disabled={busy} onClick={() => reset.mutate()}>
                {reset.isPending ? 'Создание…' : 'Да, начать заново'}
              </button>
              <button style={styles.ghost} disabled={busy} onClick={() => setConfirmReset(false)}>
                Отмена
              </button>
            </>
          )}

          {(restore.isError || reset.isError) && <div style={styles.warn}>Ошибка. Попробуйте ещё раз.</div>}
        </div>

        <div style={styles.logoutRow}>
          <button style={styles.logout} disabled={busy} onClick={() => void logout()}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Выйти из аккаунта
          </button>
        </div>
      </div>
    </div>
  );
}
